import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useQuiz } from '../../contexts/QuizContext';
import questionService, { Question } from '../../services/questionService';
import quizService from '../../services/quizService';
import ContentRenderer from '../common/ContentRenderer';

interface QuestionSelectorProps {
  selectedQuestions: Question[];
  onQuestionsChange: (questions: Question[]) => void;
  preSelectedDocument?: any; // Document type from documentService
  quizId?: number; // Quiz ID for editing
  initialTotal?: number; // total questions in quiz when editing
}

interface FilterState {
  search: string;
  document_id: string;
  type: string;
  difficulty_min: string;
  difficulty_max: string;
}

const QuestionSelector: React.FC<QuestionSelectorProps> = ({
  selectedQuestions,
  onQuestionsChange,
  preSelectedDocument,
  quizId,
  initialTotal,
}) => {
  console.log('QuestionSelector debug:', {
    selectedQuestionsCount: selectedQuestions.length,
    quizId,
    selectedQuestions: selectedQuestions.map(q => ({ id: q.id, text: q.text.substring(0, 50) }))
  });

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    type: '',
    difficulty_min: '',
    difficulty_max: '',
    document_id: ''
  });

  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(initialTotal || 0);
  // Use totalQuestions for displayedTotal
  const displayedTotal = totalQuestions;

  useEffect(() => {
    handleSearch();
  }, [currentPage, preSelectedDocument, quizId]); // Re-fetch when page, document, or quiz changes

  useEffect(() => {
    // Update selectAll state based on current page questions
    const allSelected = availableQuestions.length > 0 &&
      availableQuestions.every(q => selectedQuestions.some(sq => sq.id === q.id));
    setSelectAll(allSelected);
  }, [availableQuestions, selectedQuestions]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: '',
      difficulty_min: '',
      difficulty_max: '',
      document_id: ''
    });
    handleSearch();
  };

  const handleSearch = async () => {
    // Khi đang chỉnh sửa quiz, chỉ hiển thị danh sách câu hỏi đã chọn
    if (quizId) {
      setAvailableQuestions(selectedQuestions);
      // giữ nguyên totalQuestions = initialTotal (không thay đổi khi bỏ tích)
      return;
    }
    setQuestionsLoading(true);
    setQuestionsError(null);
    try {
      let response;
      if (quizId) {
        // Use quiz service for editing quiz
        const apiFilters: any = {
          search: filters.search || undefined,
          type: filters.type || undefined,
          page: currentPage,
          per_page: 20
        };

        if (filters.document_id && filters.document_id !== '') {
          apiFilters.document_id = parseInt(filters.document_id.toString());
        }
        if (filters.difficulty_min && filters.difficulty_min !== '') {
          apiFilters.difficulty_min = parseInt(filters.difficulty_min.toString());
        }
        if (filters.difficulty_max && filters.difficulty_max !== '') {
          apiFilters.difficulty_max = parseInt(filters.difficulty_max.toString());
        }

        response = await quizService.getAvailableQuestions(quizId, apiFilters);
        setAvailableQuestions(response.questions);
        setTotalQuestions(response.total);
      } else if (preSelectedDocument) {
        response = await questionService.getQuestions(preSelectedDocument.id);
        const startIndex = (currentPage - 1) * 20;
        const endIndex = startIndex + 20;
        setAvailableQuestions(response.questions.slice(startIndex, endIndex));
        setTotalQuestions(response.questions_count);
      } else {
        response = await questionService.getAllQuestions(currentPage);
        setAvailableQuestions(response.data.data);
        setTotalQuestions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestionsError('Đã xảy ra lỗi khi tải danh sách câu hỏi');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleQuestionToggle = (question: Question) => {
    const isSelected = selectedQuestions.some(q => q.id === question.id);

    if (isSelected) {
      // Remove question
      const newSelected = selectedQuestions.filter(q => q.id !== question.id);
      onQuestionsChange(newSelected);
    } else {
      // Add question
      const newSelected = [...selectedQuestions, question];
      onQuestionsChange(newSelected);
    }
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      // Deselect all current page questions
      const currentPageIds = availableQuestions.map(q => q.id);
      const newSelected = selectedQuestions.filter(q => !currentPageIds.includes(q.id));
      onQuestionsChange(newSelected);
    } else {
      // Select all current page questions
      const currentPageQuestions = availableQuestions.filter(q =>
        !selectedQuestions.some(sq => sq.id === q.id)
      );

      const newSelected = [...selectedQuestions, ...currentPageQuestions];
      onQuestionsChange(newSelected);
    }
  };

  const isQuestionSelected = (questionId: number) => {
    return selectedQuestions.some(q => q.id === questionId);
  };

  const getDifficultyColor = (difficulty: number | null) => {
    if (!difficulty) return 'default';
    if (difficulty <= 3) return 'success';
    if (difficulty <= 7) return 'warning';
    return 'error';
  };

  const getDifficultyText = (difficulty: number | null) => {
    if (!difficulty) return 'Chưa xác định';
    if (difficulty <= 3) return 'Dễ';
    if (difficulty <= 7) return 'Trung bình';
    return 'Khó';
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Pagination buttons
  const renderPaginationButtons = () => {
    // Khi đang chỉnh sửa quiz, ẩn phân trang vì chỉ hiển thị các câu đã chọn
    if (quizId) return null;

    const totalPages = Math.ceil(displayedTotal / 20);

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          sx={{ minWidth: 100 }}
        >
          Trang trước
        </Button>
        <Button
          variant="outlined"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          sx={{ minWidth: 100 }}
        >
          Trang sau
        </Button>
      </Box>
    );
  };

  // Luôn hiển thị danh sách availableQuestions; khi edit chúng ta đã nạp sẵn selectedQuestions vào availableQuestions
  const questionsToShow = availableQuestions;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">
            Chọn câu hỏi ({selectedQuestions.length}/{displayedTotal} câu)
          </Typography>
          {preSelectedDocument && (
            <Typography variant="body2" color="text.secondary">
              Từ tài liệu: {preSelectedDocument.name}
            </Typography>
          )}
        </Box>
        {/* <Button
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          variant={showFilters ? 'contained' : 'outlined'}
          size="small"
        >
          Bộ lọc
        </Button> */}
      </Box>

      {/* Selected Questions Summary */}
      {selectedQuestions.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            <Typography variant="body2">Đã chọn:</Typography>
            {selectedQuestions.slice(0, 3).map((question) => (
              <Chip
                key={question.id}
                label={`#${question.id}`}
                size="small"
                onDelete={() => handleQuestionToggle(question)}
              />
            ))}
            {selectedQuestions.length > 3 && (
              <Typography variant="body2">
                và {selectedQuestions.length - 3} câu khác...
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label="Tìm kiếm câu hỏi"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Loại câu hỏi</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  label="Loại câu hỏi"
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  <MenuItem value="multiple_choice">Trắc nghiệm</MenuItem>
                  <MenuItem value="true_false">Đúng/Sai</MenuItem>
                  <MenuItem value="short_answer">Trả lời ngắn</MenuItem>
                  <MenuItem value="fill_in_blank">Điền vào chỗ trống</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Độ khó tối thiểu"
                type="number"
                value={filters.difficulty_min}
                onChange={(e) => handleFilterChange('difficulty_min', e.target.value ? parseInt(e.target.value) : '')}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />

              <TextField
                fullWidth
                label="Độ khó tối đa"
                type="number"
                value={filters.difficulty_max}
                onChange={(e) => handleFilterChange('difficulty_max', e.target.value ? parseInt(e.target.value) : '')}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Box>

            <Box display="flex" gap={1} mt={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={questionsLoading}
              >
                Tìm kiếm
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
              >
                Xóa bộ lọc
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {questionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {questionsError}
        </Alert>
      )}

      {/* Loading */}
      {questionsLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Questions List */}
      {!questionsLoading && (
        <>
          {/* Select All */}
          {questionsToShow.length > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAllToggle}
                    indeterminate={
                      availableQuestions.some(q => isQuestionSelected(q.id)) && !selectAll
                    }
                  />
                }
                label={`Chọn tất cả trang này (${questionsToShow.length} câu)`}
              />
              <Typography variant="body2" color="text.secondary">
                Tìm thấy {displayedTotal} câu hỏi
              </Typography>
            </Box>
          )}

          {/* Questions */}
          {questionsToShow.length === 0 ? (
            <Box textAlign="center" py={8}>
              <QuizIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Không tìm thấy câu hỏi nào
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thử thay đổi bộ lọc hoặc tạo câu hỏi mới từ tài liệu
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Questions */}
              {questionsToShow.map((question) => (
                <Card
                  key={question.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    border: isQuestionSelected(question.id) ? 2 : 1,
                    borderColor: isQuestionSelected(question.id) ? 'primary.main' : 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => handleQuestionToggle(question)}
                >
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Checkbox
                        checked={isQuestionSelected(question.id)}
                        onChange={() => handleQuestionToggle(question)}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <Box flexGrow={1}>
                        <Typography variant="body1" gutterBottom component="div">
                          <ContentRenderer text={question.text} />
                        </Typography>

                        {/* Question Options */}
                        {question.options && question.options.length > 0 && (
                          <Box ml={2}>
                            {question.options?.map((option, index) => {
                              const isCorrect = question.correct_answer_id === `option_${index + 1}`;

                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: isCorrect ? 'success.light' : 'transparent',
                                  }}
                                >
                                  <Typography variant="body2" color={isCorrect ? 'success.main' : 'text.primary'}>
                                    {String.fromCharCode(65 + index)}.
                                  </Typography>
                                  <Box sx={{ flex: 1 }}>
                                    <ContentRenderer text={option} display="inline" />
                                  </Box>
                                  {isCorrect && (
                                    <Chip
                                      label="Đáp án đúng"
                                      size="small"
                                      color="success"
                                      sx={{ ml: 'auto' }}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}

                        {/* Question Metadata */}
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                          <Chip
                            size="small"
                            label={question.type === 'multiple_choice' ? 'Trắc nghiệm' :
                              question.type === 'true_false' ? 'Đúng/Sai' :
                                question.type === 'short_answer' ? 'Trả lời ngắn' : 'Điền vào chỗ trống'}
                            variant="outlined"
                          />

                          {question.difficulty && (
                            <Chip
                              size="small"
                              label={`${getDifficultyText(question.difficulty)}`}
                              color={getDifficultyColor(question.difficulty)}
                              variant="outlined"
                            />
                          )}

                          {question.document && (
                            <Chip
                              size="small"
                              label={question.document.name}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {renderPaginationButtons()}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default QuestionSelector;
